import { createRoot, type Root } from "react-dom/client";
import LandingApp from "./LandingApp";
import NetworkUsageScene from "./components/NetworkUsageScene";
import ParticleField, { type ParticleShape } from "./components/ParticleField";
import SiteHeader from "./components/SiteHeader";
import landingStyles from "./styles.css";

let landingRoot: Root | null = null;
let landingStyleTag: HTMLStyleElement | null = null;
let networkUsageRoot: Root | null = null;
let siteHeaderRoot: Root | null = null;
let particleFieldRoots: Root[] = [];

function ensureLandingStyles() {
  if (landingStyleTag) return;

  landingStyleTag = document.createElement("style");
  landingStyleTag.dataset.landingStyles = "true";
  landingStyleTag.textContent = landingStyles;
  document.head.appendChild(landingStyleTag);
}

function mountLanding() {
  const container = document.getElementById("cestus-landing-root");
  if (!container) return;
  if (landingRoot) return;

  ensureLandingStyles();
  landingRoot = createRoot(container);
  landingRoot.render(
    <LandingApp
      assetBasePath={container.dataset.assetBasePath || "/landing-assets"}
    />
  );
}

function unmountLanding() {
  landingRoot?.unmount();
  landingRoot = null;
  landingStyleTag?.remove();
  landingStyleTag = null;
}

// Standalone public pages render the same header as the landing scene.
function mountSiteHeader() {
  const container = document.getElementById("cestus-site-header");
  if (!container || siteHeaderRoot) return;

  siteHeaderRoot = createRoot(container);
  siteHeaderRoot.render(
    <SiteHeader
      variant="page"
      active={container.dataset.active}
      authenticated={container.dataset.authenticated === "true"}
      studioHref={container.dataset.studioPath}
    />
  );
}

function unmountSiteHeader() {
  siteHeaderRoot?.unmount();
  siteHeaderRoot = null;
}

function mountNetworkUsageScene() {
  const container = document.getElementById("network-usage-3d-root");
  if (!container || networkUsageRoot) return;

  networkUsageRoot = createRoot(container);
  networkUsageRoot.render(<NetworkUsageScene intensity={Number(container.dataset.intensity) || 0.35} />);
}

function unmountNetworkUsageScene() {
  networkUsageRoot?.unmount();
  networkUsageRoot = null;
}

// Decorative particle panels declared straight from ERB: the server owns the
// layout, the component only fills the box it is handed.
function mountParticleFields() {
  if (particleFieldRoots.length) return;

  document.querySelectorAll<HTMLElement>("[data-particle-field]").forEach((container) => {
    const root = createRoot(container);
    root.render(
      <ParticleField
        shape={(container.dataset.particleField || "sphere") as ParticleShape}
        density={Number(container.dataset.particleDensity) || 0.35}
        scale={Number(container.dataset.particleScale) || 1}
        trail={container.dataset.particleTrail || "5, 11, 19"}
      />
    );
    particleFieldRoots.push(root);
  });
}

function unmountParticleFields() {
  particleFieldRoots.forEach((root) => root.unmount());
  particleFieldRoots = [];
}

function mountAll() {
  mountLanding();
  mountSiteHeader();
  mountNetworkUsageScene();
  mountParticleFields();
}

function unmountAll() {
  unmountLanding();
  unmountSiteHeader();
  unmountNetworkUsageScene();
  unmountParticleFields();
}

document.addEventListener("turbo:load", mountAll);
document.addEventListener("turbo:before-cache", unmountAll);
